import { useEffect } from "react";

interface SEOProps {
  title: string;
  description?: string;
  image?: string;
  url?: string;
  type?: "article" | "website";
  publishedTime?: string;
  author?: string;
  tags?: string[];
}

export function SEO({
  title,
  description,
  image,
  url,
  type = "website",
  publishedTime,
  author = "Brian J Kopp",
  tags = [],
}: SEOProps) {
  useEffect(() => {
    const siteUrl = window.location.origin;
    const pageUrl = url || window.location.href;
    const fullTitle = `${title} | Brian J Kopp`;
    const defaultDescription =
      "Insights on digital transformation, strategy, technology, and leadership in the modern enterprise.";
    const metaDescription = description || defaultDescription;

    // Update document title
    document.title = fullTitle;

    // Helper to set or create meta tags
    const setMetaTag = (
      attribute: "name" | "property",
      key: string,
      content: string
    ) => {
      let element = document.querySelector(
        `meta[${attribute}="${key}"]`
      ) as HTMLMetaElement;
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    // Basic meta tags
    setMetaTag("name", "description", metaDescription);
    if (tags.length > 0) {
      setMetaTag("name", "keywords", tags.join(", "));
    }
    setMetaTag("name", "author", author);

    // Open Graph tags
    setMetaTag("property", "og:title", fullTitle);
    setMetaTag("property", "og:description", metaDescription);
    setMetaTag("property", "og:type", type);
    setMetaTag("property", "og:url", pageUrl);
    setMetaTag("property", "og:site_name", "Brian J Kopp");
    if (image) {
      setMetaTag("property", "og:image", image.startsWith("http") ? image : `${siteUrl}${image}`);
      setMetaTag("property", "og:image:width", "1200");
      setMetaTag("property", "og:image:height", "630");
    }
    if (type === "article" && publishedTime) {
      setMetaTag("property", "article:published_time", publishedTime);
      setMetaTag("property", "article:author", author);
      tags.forEach((tag, index) => {
        setMetaTag("property", `article:tag:${index}`, tag);
      });
    }

    // Twitter Card tags
    setMetaTag("name", "twitter:card", image ? "summary_large_image" : "summary");
    setMetaTag("name", "twitter:title", fullTitle);
    setMetaTag("name", "twitter:description", metaDescription);
    if (image) {
      setMetaTag("name", "twitter:image", image.startsWith("http") ? image : `${siteUrl}${image}`);
    }

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", pageUrl);

    // Cleanup function to reset to defaults when unmounting
    return () => {
      document.title = "Brian J Kopp | Digital Transformation Executive";
    };
  }, [title, description, image, url, type, publishedTime, author, tags]);

  return null;
}
